import React, { CSSProperties, ReactNode, useCallback, useMemo, useState } from "react";

import type { ModalProps } from "@mapmycustomers/ui";
import { Modal } from "@mapmycustomers/ui";

import Placement from "@app/enum/Placement";

const DEFAULT_GAP = 8; // 8px

interface Props extends ModalProps {
  element?: HTMLElement | null;
  placement: Placement;
}

const AlignedModal: React.FC<Props> = ({
  element,
  modalRender,
  placement,
  style,
  ...modalProps
}) => {
  const [modalContainer, setModalContainer] = useState<HTMLDivElement | null>(null);

  const customModalRender = useCallback(
    (modal: ReactNode) => {
      const node = <div ref={setModalContainer}>{modal}</div>;
      return modalRender ? modalRender(node) : node;
    },
    [modalRender]
  );

  const customStyles: CSSProperties | undefined = useMemo(() => {
    const elementRect = element?.getBoundingClientRect();
    const modalRect = modalContainer?.getBoundingClientRect();
    if (!elementRect || !modalRect) {
      return style;
    }

    // Note: current code won't re-position modal if modal dimensions changes. If you need that,
    // feel free to add position/size observer and update useMemo's deps
    const customStyle: CSSProperties = {
      margin: "initial", // reset margin because otherwise it tries to position modal in center (0 auto)
      padding: "0", // also reset padding because otherwise it affects positioning
    };
    switch (placement) {
      case Placement.BOTTOM:
        customStyle.left = elementRect.left + elementRect.width / 2 - modalRect.width / 2;
        customStyle.top = elementRect.bottom + DEFAULT_GAP;
        break;
      case Placement.BOTTOM_LEFT:
        customStyle.left = elementRect.left;
        customStyle.top = elementRect.bottom + DEFAULT_GAP;
        break;
      case Placement.BOTTOM_RIGHT:
        customStyle.left = elementRect.right - modalRect.width;
        customStyle.top = elementRect.bottom + DEFAULT_GAP;
        break;
      case Placement.LEFT:
        customStyle.left = elementRect.left - modalRect.width - DEFAULT_GAP;
        customStyle.top = elementRect.top + elementRect.height / 2 - modalRect.height / 2;
        break;
      case Placement.LEFT_BOTTOM:
        customStyle.left = elementRect.left - modalRect.width - DEFAULT_GAP;
        customStyle.top = elementRect.bottom - modalRect.height;
        break;
      case Placement.LEFT_TOP:
        customStyle.left = elementRect.left - modalRect.width - DEFAULT_GAP;
        customStyle.top = elementRect.top;
        break;
      case Placement.RIGHT:
        customStyle.left = elementRect.right + DEFAULT_GAP;
        customStyle.top = elementRect.top + elementRect.height / 2 - modalRect.height / 2;
        break;
      case Placement.RIGHT_BOTTOM:
        customStyle.left = elementRect.right + DEFAULT_GAP;
        customStyle.top = elementRect.bottom - modalRect.height;
        break;
      // Note: only leftBottom positioning was tested, feel free to correct calculations :)
      // We expect same behavior as for placement prop in AtnD's tooltip or popover:
      // https://ant.design/components/popover/
      case Placement.RIGHT_TOP:
        customStyle.left = elementRect.right + DEFAULT_GAP;
        customStyle.top = elementRect.top;
        break;
      case Placement.TOP:
        customStyle.left = elementRect.left + elementRect.width / 2 - modalRect.width / 2;
        customStyle.top = elementRect.top - DEFAULT_GAP - modalRect.height;
        break;
      case Placement.TOP_LEFT:
        customStyle.left = elementRect.left;
        customStyle.top = elementRect.top - DEFAULT_GAP - modalRect.height;
        break;
      case Placement.TOP_RIGHT:
        customStyle.left = elementRect.right - modalRect.width;
        customStyle.top = elementRect.top - DEFAULT_GAP - modalRect.height;
        break;
    }

    return { ...style, ...customStyle };
  }, [element, modalContainer, placement, style]);

  return (
    <Modal centered={false} modalRender={customModalRender} style={customStyles} {...modalProps} />
  );
};

export default AlignedModal;
